import { gql, useQuery } from '@apollo/client';
import React from 'react';

import { ErrorModal } from 'src/components/common/ErrorModal';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { GraphRef, graphRefToString } from '../../hooks/useGraphRef';

const buildErrorModalQuery = gql`
  query BuildErrorModalQuery($graphRef: ID!, $graphVariant: String!) {
    variant(ref: $graphRef) {
      ... on GraphVariant {
        id
        latestLaunch {
          id
          build {
            id
            result {
              ... on BuildFailure {
                errorMessages {
                  message
                  code
                }
              }
            }
          }
        }
        graph {
          id
          mostRecentCompositionPublish(graphVariant: $graphVariant) {
            graphCompositionID
            errors {
              code
              locations {
                column
                line
              }
              message
            }
          }
        }
      }
    }
  }
`;

export const BuildErrorModal = ({
  isOpen,
  onClose,
  graphRef,
}: {
  isOpen: boolean;
  onClose: () => void;
  graphRef: GraphRef;
}) => {
  const graphRefString = graphRefToString(graphRef);
  const { data, loading } = useQuery<
    GraphQLTypes.BuildErrorModalQuery,
    GraphQLTypes.BuildErrorModalQueryVariables
  >(buildErrorModalQuery, {
    variables: {
      graphRef: graphRefString,
      graphVariant: graphRef.graphVariant,
    },
    context: appLinkContext({
      catchErrors: [ignorePermissionsErrors],
    }),
  });

  if (data?.variant?.__typename === 'InvalidRefFormat') {
    throw new Error(`Invalid graph ref: ${graphRefString}`);
  }

  const build = data?.variant?.latestLaunch?.build;
  const errors =
    build?.result?.__typename === 'BuildFailure'
      ? build.result.errorMessages
      : (data?.variant?.graph.mostRecentCompositionPublish?.errors ?? []);

  return (
    <ErrorModal
      isOpen={isOpen}
      title={
        <>
          {graphRefString} has{' '}
          <span className="text-red">
            {loading ? '' : errors.length}{' '}
            {format.pluralize(loading ? 2 : errors.length, 'composition error')}
          </span>
        </>
      }
      description="Subgraph errors are preventing composition occuring on your latest subgraph changes. Your API Schema will not contain these changes until the errors are resolved. You can push fixes from your deployment pipeline."
      onClose={onClose}
      loading={loading}
      errors={errors.map((e) => {
        const serviceRegex = e.message.match(/\[(.*)\]((.|\n)+)/);
        if (isMinLengthArray(3, serviceRegex)) {
          const [, service, message] = serviceRegex;
          return {
            message,
            code: e.code,
            group: service,
          };
        }
        return {
          message: e.message,
          code: e.code,
          group: '[subgraph unknown]',
        };
      })}
    />
  );
};
